import type {
	ProductEntity,
	ProductsQueryItemModel,
} from '@/entities/assortment'
import type { DynamicAnalyticsData, MetricOption } from '@/entities/bi'

import { useState } from 'react'
import { FeatureFlag } from '@openfeature/react-sdk'
import { addMonths, format } from 'date-fns'
import { any, find, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { MidTermForecastPanel } from '@/features/mid-term-forecast'

import {
	DateAggregation,
	formatMetrics,
	useBIAnalyticQuery,
	useMetricsQuery,
	zipSeries,
} from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import overview from '@/shared/assets/img/svg/overview.svg'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Label } from '@/shared/components/ui/label'
import { Loader } from '@/shared/components/ui/loader'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { DateFormat } from '@/shared/tools/dates'

import PerfomanceChart from './PerfomanceChart'

const getSerieData = (
	data: DynamicAnalyticsData[] | undefined,
	prop: keyof DynamicAnalyticsData
) =>
	data?.map((d, i) => ({
		x: i,
		y: d[prop],
	})) || []
type Props = {
	content: ProductEntity | ProductsQueryItemModel
}
export function PerfomanceTab({ content }: Props) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const optimizationGroupId = content.optimization_group_id
	const { data: metrics, ...metricsQuery } = useMetricsQuery<MetricOption[]>({
		placeholderData: [],
		select: (data) => formatMetrics(data, t),
	})
	const { data, ...dataQuery } = useBIAnalyticQuery<DynamicAnalyticsData[]>(
		{
			currency,
			metrics: [...(metrics || []).map(prop('value'))] as string[],
			dimensions: [],
			date_aggregation: DateAggregation.WeekKey,
			start_date: format(addMonths(new Date(), -1), DateFormat.system),
			end_date: format(new Date(), DateFormat.system),
			filters: (content as ProductEntity).sku
				? [
						{
							name: 'optimization_group_id',
							operation: 'is',
							value: optimizationGroupId,
						},
						{
							name: 'line_id',
							operation: 'is',
							value: content.line_id,
						},
						{
							name: 'sku',
							operation: 'is',
							value: (content as ProductEntity).sku,
						},
					]
				: [
						{
							name: 'optimization_group_id',
							operation: 'is',
							value: optimizationGroupId,
						},
						{
							name: 'line_id',
							operation: 'is',
							value: content.line_id,
						},
					],
		},
		{
			enabled: !!content.line_id && !!optimizationGroupId,
			select: (data) => zipSeries<DynamicAnalyticsData>(data?.meta, data?.data),
		}
	)
	const [businessMetric, setBusinessMetric] = useState<string>('revenue')
	const [priceMetric, setPriceMetric] = useState<string>('average_cost')

	const activeBusinessMetric = find({ value: businessMetric }, metrics)
	const activePriceMetric = find({ value: priceMetric }, metrics)

	const mainSeries = [activeBusinessMetric!, activePriceMetric!]
		.filter(Boolean)
		.map(
			({ label, value, color }, yAxis) =>
				({
					marker: {
						symbol: 'circle',
					},
					name: label,
					color,
					yAxis,
					data: getSerieData(data, value as keyof DynamicAnalyticsData),
				}) as Highcharts.SeriesOptionsType
		)

	const businessMetrics =
		metricsQuery.isLoading || dataQuery.isLoading ? (
			<div className='relative flex h-52 items-center justify-center'>
				<Loader />
			</div>
		) : (
			<>
				<div className='grid grid-cols-2 gap-6'>
					<div className='flex flex-col gap-1.5'>
						<Label>{t('overview_business_metric')}</Label>
						<Select value={businessMetric} onValueChange={setBusinessMetric}>
							<SelectTrigger className='w-full'>
								<SelectValue placeholder={t('select_metric')} />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{(
										metrics?.filter(({ type }) => type === 'business') || []
									).map(({ value, label, color }) => (
										<SelectItem value={value} key={value}>
											<span
												className='size-2 shrink-0 rounded-full'
												style={{ backgroundColor: color }}
											/>
											{label}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
					<div className='flex flex-col gap-1.5'>
						<Label>{t('overview_price_metric')}</Label>
						<Select value={priceMetric} onValueChange={setPriceMetric}>
							<SelectTrigger className='w-full'>
								<SelectValue placeholder={t('select_metric')} />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{(
										metrics?.filter(({ type }) => type === 'pricing') || []
									).map(({ value, label, color }) => (
										<SelectItem value={value} key={value}>
											<span
												className='size-2 shrink-0 rounded-full'
												style={{ backgroundColor: color }}
											/>
											{label}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
				</div>
				{any(
					({ data }: { data: Highcharts.PointOptionsObject[] | undefined }) =>
						!!data?.length,
					mainSeries
				) ? (
					<PerfomanceChart
						chartData={data || []}
						series={[activeBusinessMetric!, activePriceMetric!]
							.filter(Boolean)
							.map(({ label, value, color }, yAxis) => ({
								id: value,
								name: label,
								color,
								yAxis,
							}))}
					/>
				) : (
					<Empty>
						<EmptyHeader>
							<EmptyMedia>
								<img src={overview} className='h-52' alt='No data' />
							</EmptyMedia>
							<EmptyDescription>
								{t('business_metric_no_data')}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				)}
			</>
		)

	return (
		<div className='grid gap-5 pt-4 pb-16'>
			<FeatureFlag flagKey='po_midterm_predict' defaultValue={false}>
				<MidTermForecastPanel
					lineId={content.line_id}
					optimizationGroupId={optimizationGroupId}
				/>
			</FeatureFlag>
			<div className='grid gap-4' data-testid='perf-business-metrics'>
				{businessMetrics}
			</div>
		</div>
	)
}
